import { format, parseISO, addDays, startOfWeek } from 'date-fns';

// Normalise ISO / datetime / DD/MM/YYYY strings to 'YYYY-MM-DD' (or null)
export const toDayKey = (value) => {
  if (!value || typeof value !== 'string') return null;
  const v = value.trim();
  const dmy = v.match(/^(\d{1,2})[/-](\d{1,2})[/-](\d{4})/);
  if (dmy) return `${dmy[3]}-${dmy[2].padStart(2, '0')}-${dmy[1].padStart(2, '0')}`;
  if (/^\d{4}-\d{2}-\d{2}/.test(v)) return v.slice(0, 10);
  const d = parseISO(v);
  return isNaN(d) ? null : format(d, 'yyyy-MM-dd');
};

export const dayKey = (d) => format(d, 'yyyy-MM-dd');

// Normalised [startKey, endKey] for an event (end never before start)
export const eventRange = (e) => {
  const s = toDayKey(e.start);
  if (!s) return null;
  const en = toDayKey(e.end);
  return [s, en && en > s ? en : s];
};

// True if `day` falls between start and (optional) end date, inclusive
export const spansDay = (start, end, day) => {
  const r = eventRange({ start, end });
  if (!r) return false;
  const k = dayKey(day);
  return k >= r[0] && k <= r[1];
};

export const weekDays = (anyDay) => {
  const s = startOfWeek(anyDay, { weekStartsOn: 1 });
  return Array.from({ length: 7 }, (_, i) => addDays(s, i));
};

// '09:30', '9:30 AM', '17:00:00' → minutes from midnight
export const parseTime = (t) => {
  if (!t || typeof t !== 'string') return null;
  const m = t.trim().match(/^(\d{1,2}):(\d{2})(?::\d{2})?\s*(am|pm)?$/i);
  if (!m) return null;
  let h = Number(m[1]);
  const ap = (m[3] || '').toLowerCase();
  if (ap === 'pm' && h < 12) h += 12;
  if (ap === 'am' && h === 12) h = 0;
  return h * 60 + Number(m[2]);
};

export const fmtMinutes = (mins) => {
  const h = Math.floor(mins / 60) % 24;
  const m = mins % 60;
  return `${((h + 11) % 12) + 1}:${String(m).padStart(2, '0')} ${h < 12 ? 'AM' : 'PM'}`;
};

// Greedy lane packing of events into a 7-day week row
export const layoutWeekBars = (events, days) => {
  const first = dayKey(days[0]);
  const last = dayKey(days[6]);
  const keys = days.map(dayKey);
  const rows = events
    .map((e) => ({ e, r: eventRange(e) }))
    .filter(({ r }) => r && r[0] <= last && r[1] >= first)
    .sort((a, b) => a.r[0].localeCompare(b.r[0]) || b.r[1].localeCompare(a.r[1]));
  const laneEnds = [];
  return rows.map(({ e, r }) => {
    const startCol = r[0] < first ? 0 : keys.indexOf(r[0]);
    const endCol = r[1] > last ? 6 : keys.indexOf(r[1]);
    let lane = laneEnds.findIndex((end) => end < startCol);
    if (lane === -1) { lane = laneEnds.length; laneEnds.push(endCol); } else laneEnds[lane] = endCol;
    return { event: e, startCol, span: endCol - startCol + 1, lane, contBefore: r[0] < first, contAfter: r[1] > last };
  });
};

export const sessionTrainers = (s) => {
  const names = [s.trainer_name, s.trainer_2_name];
  (s.session_trainers || []).forEach((t) => names.push(t?.name || t?.trainer_name));
  (s.conference_trainers || []).forEach((t) => names.push(t?.trainer_name || t?.name));
  return [...new Set(names.map((n) => (n || '').trim()).filter(Boolean))];
};

// Tailwind chip classes used by legacy status palettes → hex
export const CHIP_HEX = {
  'bg-green-500': '#22C55E', 'bg-red-500': '#EF4444', 'bg-blue-500': '#3B82F6', 'bg-sky-500': '#0EA5E9',
  'bg-orange-500': '#F97316', 'bg-gray-400': '#9CA3AF', 'bg-purple-500': '#A855F7', 'bg-violet-500': '#8B5CF6',
};
